import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { downloadPdf } from "@/pages/ClientsPage";
import { FilePdf, PaperPlaneTilt, ChartBar, UsersThree, Lifebuoy, DownloadSimple } from "@phosphor-icons/react";

const C = { navy: "#0B1E3B", red: "#E01A2E", cream: "#EFECE4", body: "#F7F5F0", slate: "#5B6B7E", green: "#1E9E63", ink: "#1A1E24", amber: "#F5A623" };

const Card = ({ children, testid, style }) => (
  <div data-testid={testid} style={{ background: "#fff", border: "1px solid rgba(26,30,36,0.08)", borderRadius: 12, padding: 22, marginBottom: 18, ...style }}>{children}</div>
);
const Head = ({ icon: Icon, title, right }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}><Icon size={18} color={C.navy} /><div style={{ fontSize: 15, fontWeight: 700, color: C.ink }}>{title}</div></div>
    {right}
  </div>
);
const Kpi = ({ label, value, accent }) => (
  <div style={{ background: "#fff", border: "1px solid rgba(26,30,36,0.08)", borderRadius: 10, padding: "16px 18px", flex: 1, minWidth: 120 }}>
    <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: C.slate, fontFamily: "'JetBrains Mono', monospace" }}>{label}</div>
    <div style={{ marginTop: 6, fontSize: 26, fontWeight: 700, color: accent || C.ink, fontVariantNumeric: "tabular-nums" }}>{value}</div>
  </div>
);

export default function ReportsPage() {
  const [age, setAge] = useState(null);
  const [help, setHelp] = useState(null);
  const [clients, setClients] = useState([]);
  const [busy, setBusy] = useState(null);
  const [month, setMonth] = useState("");

  useEffect(() => {
    api.get("/reports/subscribers/age-analysis").then((r) => setAge(r.data)).catch((e) => console.error("age analysis load failed", e));
    api.get("/reports/subscribers/help-requests").then((r) => setHelp(r.data)).catch((e) => console.error("help requests load failed", e));
    api.get("/clients").then((r) => setClients(r.data)).catch((e) => console.error("clients load failed", e));
  }, []);

  const monthQ = month ? `?month=${month}` : "";
  const monthTag = month || "all-time";
  const dl = async (url, file, key) => {
    setBusy(key);
    try { await downloadPdf(url, file); toast.success("Report downloaded"); }
    catch (e) { toast.error("Download failed", { description: e.response?.data?.detail || e.message }); }
    finally { setBusy(null); }
  };
  const emailClient = async (c) => {
    setBusy(`email-${c.id}`);
    try { const r = await api.post(`/reports/client/${c.id}/email${monthQ}`); toast.success(`Report emailed to ${r.data.to || c.contact_email}`); }
    catch (e) { toast.error("Email failed", { description: e.response?.data?.detail || e.message }); }
    finally { setBusy(null); }
  };

  const maxBucket = age ? Math.max(...age.buckets.map((b) => b.count), 1) : 1;
  const maxYear = help ? Math.max(...help.by_year.map((y) => y.count), 1) : 1;
  const maxTenure = age?.tenure ? Math.max(...age.tenure.buckets.map((b) => b.count), 1) : 1;

  return (
    <div style={{ background: C.body, minHeight: "100vh", padding: 32 }} data-testid="reports-page">
      <div style={{ fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: C.slate, fontFamily: "'JetBrains Mono', monospace", display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ width: 22, height: 1, background: C.red }} /> Subscriber analytics
      </div>
      <h1 style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 34, fontWeight: 500, letterSpacing: "-0.02em", color: C.ink, margin: "6px 0 4px" }}>Reports</h1>
      <div style={{ fontSize: 13, color: C.slate, marginBottom: 22 }}>Profiles, help-request activity and age analysis across the subscriber base.</div>

      {/* Profile details */}
      <Card testid="report-profiles">
        <Head icon={UsersThree} title="Subscriber profile details"
          right={<button onClick={() => dl("/reports/subscribers/profiles.csv", "resq1-subscriber-profiles.csv", "csv")} disabled={busy === "csv"} data-testid="report-profiles-export"
            style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 14px", background: C.ink, color: "#fff", border: 0, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            <DownloadSimple size={15} /> {busy === "csv" ? "Preparing…" : "Export CSV"}</button>} />
        {age ? (
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <Kpi label="Total subscribers" value={age.total.toLocaleString()} />
            <Kpi label="With date of birth" value={age.with_dob.toLocaleString()} accent={C.green} />
            <Kpi label="Male" value={age.gender.Male.toLocaleString()} accent={C.navy} />
            <Kpi label="Female" value={age.gender.Female.toLocaleString()} accent={C.red} />
          </div>
        ) : <div style={{ fontSize: 13, color: C.slate }}>Loading…</div>}
        <div style={{ fontSize: 12, color: C.slate, marginTop: 12 }}>Full roster (name, ID, age, gender, cell, insurer, plan, app status) downloads as CSV.</div>
      </Card>

      {/* Age analysis */}
      <Card testid="report-age">
        <Head icon={ChartBar} title="Age analysis of subscribers"
          right={age && age.average_age != null ? <span style={{ fontSize: 12, color: C.slate }}>Average age <b style={{ color: C.ink, fontSize: 15 }}>{age.average_age}</b></span> : null} />
        {age ? (
          <div style={{ display: "grid", gap: 10 }}>
            {age.buckets.map((b) => (
              <div key={b.label} style={{ display: "flex", alignItems: "center", gap: 12 }} data-testid={`age-bucket-${b.label}`}>
                <div style={{ width: 54, fontSize: 12, fontWeight: 600, color: C.ink, textAlign: "right" }}>{b.label}</div>
                <div style={{ flex: 1, background: C.cream, borderRadius: 6, height: 22, overflow: "hidden" }}>
                  <div style={{ width: `${Math.max((b.count / maxBucket) * 100, 2)}%`, height: "100%", background: C.navy, borderRadius: 6, transition: "width .4s" }} />
                </div>
                <div style={{ width: 56, fontSize: 13, fontWeight: 700, color: C.ink, fontFamily: "'JetBrains Mono', monospace", textAlign: "right" }}>{b.count.toLocaleString()}</div>
              </div>
            ))}
            {age.unknown > 0 && <div style={{ fontSize: 11, color: C.slate, marginTop: 4 }}>{age.unknown.toLocaleString()} subscribers had no valid date of birth in their ID and are excluded from the buckets.</div>}
          </div>
        ) : <div style={{ fontSize: 13, color: C.slate }}>Loading…</div>}
      </Card>

      {/* Tenure — how long with us */}
      <Card testid="report-tenure">
        <Head icon={ChartBar} title="Tenure — how long subscribers have been with us"
          right={age?.tenure?.average_years != null ? <span style={{ fontSize: 12, color: C.slate }}>Average tenure <b style={{ color: C.ink, fontSize: 15 }}>{age.tenure.average_years} yrs</b></span> : null} />
        {age?.tenure ? (
          <div style={{ display: "grid", gap: 10 }}>
            {age.tenure.with_join_date === 0 && <div style={{ fontSize: 13, color: C.slate }} data-testid="tenure-empty">No join dates on record yet — tenure will populate once the date backfill runs.</div>}
            {age.tenure.with_join_date > 0 && age.tenure.buckets.map((b) => (
              <div key={b.label} style={{ display: "flex", alignItems: "center", gap: 12 }} data-testid={`tenure-bucket-${b.label}`}>
                <div style={{ width: 66, fontSize: 12, fontWeight: 600, color: C.ink, textAlign: "right" }}>{b.label}</div>
                <div style={{ flex: 1, background: C.cream, borderRadius: 6, height: 22, overflow: "hidden" }}>
                  <div style={{ width: b.count ? `${Math.max((b.count / maxTenure) * 100, 2)}%` : "0%", height: "100%", background: C.green, borderRadius: 6, transition: "width .4s" }} />
                </div>
                <div style={{ width: 56, fontSize: 13, fontWeight: 700, color: C.ink, fontFamily: "'JetBrains Mono', monospace", textAlign: "right" }}>{b.count.toLocaleString()}</div>
              </div>
            ))}
            {age.tenure.unknown > 0 && <div style={{ fontSize: 11, color: C.slate, marginTop: 4 }}>{age.tenure.unknown.toLocaleString()} subscribers have no join date on record and are excluded.</div>}
          </div>
        ) : <div style={{ fontSize: 13, color: C.slate }}>Loading…</div>}
      </Card>

      {/* Help requests */}
      <Card testid="report-help">
        <Head icon={Lifebuoy} title="Help-request activity" />
        {help ? (
          <>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 18 }}>
              <Kpi label="Total requests" value={help.total.toLocaleString()} accent={C.red} />
              <Kpi label="Acknowledged" value={`${help.ack_pct}%`} accent={C.green} />
              <Kpi label="Completed" value={`${help.completed_pct}%`} accent={C.green} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 22 }}>
              <div>
                <div style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: C.slate, fontFamily: "'JetBrains Mono', monospace", marginBottom: 8 }}>Requests by year</div>
                {help.by_year.map((y) => (
                  <div key={y.year} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                    <div style={{ width: 44, fontSize: 12, color: C.ink }}>{y.year}</div>
                    <div style={{ flex: 1, background: C.cream, borderRadius: 5, height: 16 }}>
                      <div style={{ width: `${Math.max((y.count / maxYear) * 100, 2)}%`, height: "100%", background: C.red, borderRadius: 5 }} />
                    </div>
                    <div style={{ width: 40, fontSize: 12, fontWeight: 600, textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }}>{y.count}</div>
                  </div>
                ))}
              </div>
              <div>
                <div style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: C.slate, fontFamily: "'JetBrains Mono', monospace", marginBottom: 8 }}>Repeat callers</div>
                {help.repeat_callers.map((p, i) => (
                  <div key={p.key || i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid rgba(26,30,36,0.05)", fontSize: 13 }} data-testid={`repeat-caller-${i}`}>
                    <span style={{ color: C.ink }}>{p.name}</span>
                    <span style={{ fontWeight: 700, color: C.red, fontFamily: "'JetBrains Mono', monospace" }}>{p.count}×</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : <div style={{ fontSize: 13, color: C.slate }}>Loading…</div>}
      </Card>

      {/* Client performance (secondary) */}
      <Card testid="report-clients">
        <Head icon={ChartBar} title="Client (partner) performance reports"
          right={<div data-testid="report-month-picker" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input type="month" value={month} max={new Date().toISOString().slice(0, 7)} onChange={(e) => setMonth(e.target.value)} data-testid="report-month-input"
              style={{ padding: "8px 10px", border: "1px solid rgba(26,30,36,0.16)", borderRadius: 8, fontSize: 13, background: "#fff", color: C.ink }} />
            {month && <button onClick={() => setMonth("")} data-testid="report-month-clear" style={{ padding: "8px 12px", border: "1px solid rgba(26,30,36,0.16)", borderRadius: 8, fontSize: 12, fontWeight: 600, background: "#fff", color: C.slate, cursor: "pointer" }}>All-time</button>}
          </div>} />
        {clients.length === 0 && <div style={{ fontSize: 13, color: C.slate }}>No API clients yet.</div>}
        {clients.map((c) => (
          <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderTop: "1px solid rgba(26,30,36,0.06)" }} data-testid={`report-client-${c.id}`}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: C.ink }}>{c.name}</div>
              <div style={{ fontSize: 11, color: C.slate }}>{c.contact_email || "no contact email"}</div>
            </div>
            <button onClick={() => dl(`/reports/client/${c.id}${monthQ}`, `resqconnect-report-${c.name.toLowerCase().replace(/ /g, "-")}-${monthTag}.pdf`, `dl-${c.id}`)} disabled={busy === `dl-${c.id}`} data-testid={`report-client-download-${c.id}`}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 13px", background: `${C.red}0f`, color: C.red, border: `1px solid ${C.red}33`, borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" }}><FilePdf size={14} /> PDF</button>
            <button onClick={() => emailClient(c)} disabled={busy === `email-${c.id}`} data-testid={`report-client-email-${c.id}`}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 13px", background: `${C.navy}0d`, color: C.navy, border: `1px solid ${C.navy}22`, borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" }}><PaperPlaneTilt size={14} /> Email</button>
          </div>
        ))}
      </Card>
    </div>
  );
}
